import { Component, OnInit, OnChanges, Input, Output, EventEmitter } from '@angular/core';
import { Validators, FormBuilder, FormGroup } from '@angular/forms';
import { UtilityService } from 'app/services/utility/utility.service';
import { DecimalPipe } from '@angular/common';
import { ValidatorsService } from 'app/services/validators/validators.service';
import { SelectableAmount } from '../../models/selectable-amount';
import { distinctUntilChanged } from 'rxjs/operators';
import { CardInfo } from '../../models/card-info';
import { PaymentForm } from '../../models/forms';
import { ChargeHttpService } from 'app/services/api/charge-http.service';
import { HttpError } from 'app/static/http-error';
import { HttpBaseResponse } from 'app/services/http/models/http-base-response';
import { API_RESPONSE_CODES, END_POINTS } from 'app/static/api';
import { SnackbarService } from 'app/services/snackbar/snackbar.service';
import { SendPaymentDataResponse } from '../../models/api-response';
import { environment } from 'environments/environment';

@Component({
  selector: 'app-payment-form',
  templateUrl: './payment-form.component.html',
  styleUrls: ['./payment-form.component.scss'],
  providers: [DecimalPipe]
})
export class PaymentFormComponent implements OnInit, OnChanges {

  @Input() cardInfo: CardInfo;

  @Output() submitted: EventEmitter<boolean>;

  form: FormGroup;

  selectableAmounts: SelectableAmount[];

  isSubmitting: boolean;

  maxAmount: number; // will be calculated after cardInfo fetch

  readonly minAmount: number;

  constructor(
    private readonly chargeHttp: ChargeHttpService,
    private readonly validators: ValidatorsService,
    private readonly snackbar: SnackbarService,
    private readonly decimalPipe: DecimalPipe,
    private readonly formBuilder: FormBuilder,
    private readonly utility: UtilityService,
  ) {

    this.minAmount = 5 * Math.pow(10, 4); // 50,000 rials
    this.maxAmount = 5 * Math.pow(10, 8); // 500,000,000 rials

    this.initForm();

    this.watchFormValueChange();

    this.submitted = new EventEmitter();

    this.selectableAmounts = [
      // 50,000 rials
      { amount: 50000, isSelected: false, id: 1 },
      // 100,000 rials
      { amount: 100000, isSelected: false, id: 2 },
      // 200,000 rials
      { amount: 200000, isSelected: false, id: 3 },
      // 500,000 rials
      { amount: 500000, isSelected: false, id: 4 },
    ];
  }

  ngOnInit() {
  }

  ngOnChanges() {
    if (this.cardInfo) {
      this.updateForm();
    }
  }


  onAmountSelection(item: SelectableAmount): void {
    if (!item.isSelected) {
      item.isSelected = true;

      this.patchAmountValue(this.decimalPipe.transform(item.amount));

      this.selectableAmounts.forEach(element => {
        if (element.id !== item.id) {
          element.isSelected = false;
        }
      });
    }
  }


  onSubmit(): void {
    if (this.form.valid) {
      this.isSubmitting = true;
      this.submitted.emit(this.isSubmitting);

      this.form.disable();

      const form = <PaymentForm>this.form.getRawValue();
      form.amount = this.utility.removeCommasFromNumber(form.amount);

      this.sendPaymentData(form);
    }
  }


  private sendPaymentData(form: PaymentForm) {
    this.chargeHttp.sendPaymentData(form)
      .subscribe(
        res => this.onSendPaymentDataResponse(res),
        (err: HttpError) => this.onSendPaymentDataError(err),
      );
  }


  private onSendPaymentDataResponse(res: SendPaymentDataResponse): void {
    if (res.status === API_RESPONSE_CODES.SUCCESS) {
      // redirect to IPG
      this.redirect(res.data);
    } else {
      this.snackbar.default(res.message);

      this.isSubmitting = false;
      this.submitted.emit(this.isSubmitting);
      this.form.enable()
    }
  }


  private onSendPaymentDataError(err: HttpError): void {
    this.snackbar.default(err.message);
    this.form.enable();
    this.isSubmitting = false;
    this.submitted.emit(this.isSubmitting);
  }


  private initForm() {
    this.form = this.formBuilder.group({
      amount: ['', Validators.compose([Validators.required])],
      cardno: ['']
    });
  }


  private watchFormValueChange() {
    this.form.get('amount').valueChanges
      .pipe(distinctUntilChanged())
      .subscribe(value => {

        for (const element of this.selectableAmounts) {
          if (this.utility.removeCommasFromNumber(value) === element.amount) {
            // if current value is equal with any of preDefined values
            // mark the preDefined element is selected
            this.onAmountSelection(element);
            return;
          } else {
            // unselect all preDefined Values
            element.isSelected = false;
          }
        }

      });
  }


  private updateForm() {
    // patch cardno formControl value
    this.form.get('cardno').patchValue(this.cardInfo.cardno);

    if (this.cardInfo.remain) {
      // set maximum possible value as cardinfo.remain
      this.maxAmount = this.cardInfo.remain;
    }

    this.form.get('amount').setValidators(
      Validators.compose([
        Validators.required,
        this.validators.min(this.minAmount),
        this.validators.max(this.maxAmount)
      ])
    );
  }


  private patchAmountValue(amount: string): void {
    this.form.patchValue({
      amount: amount
    });
  }


  /* redirect to IPG page */
  private redirect(payid: string): void {
    const payUrl = `${environment.baseUrl}${END_POINTS.pay}`;
    window.location.href = `${payUrl}/${payid}`;
  }

}
